import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, ChevronDown } from "lucide-react";
import { EcologiFooterBadge } from "@/components/EcologiBadge";
import PaymentMethods from "@/components/PaymentMethods";
import AnimatedAccordion from "@/components/AnimatedAccordion";
import { SocialIcons } from "@/components/SocialLinks";
import BCorpMark from "@/components/BCorpMark";
import WhatsAppIcon from "@/components/WhatsAppIcon";
import analytics from "@/utils/analytics";
import { whatsAppUrl, WHATSAPP_NUMBER_DISPLAY } from "@/utils/whatsapp";
import { OPEN_PREFS_EVENT } from "@/components/CookieConsent";

// Consent-withdrawal entry point: reopens the cookie preference centre
// (handled by the singleton <CookieConsent /> mounted in App.js) with the
// user's current choice pre-filled, so consent can be changed/withdrawn.
function openCookiePreferences() {
  window.dispatchEvent(new CustomEvent(OPEN_PREFS_EVENT));
}

// Module-scope constant — prevents a hydration mismatch if a deploy's
// react-snap prerender and the user's runtime fall on opposite sides of a
// New Year's Eve midnight. The value is frozen at module-load time (which
// for the prerender is the build moment, and for the client is the bundle's
// first import) instead of being recomputed each render.
const CURRENT_YEAR = new Date().getFullYear();

// Map of Work With Us link paths → { itemName, itemType } for tracking.
const PARTNER_MAP = {
  "/switch-and-save":          { itemName: "switch_and_save",               itemType: "partner_service" },
  "/partner-programme":        { itemName: "partner_programme",             itemType: "partner_programme" },
  "/epos-partner-calculator":  { itemName: "epos_partner_calculator",       itemType: "partner_calculator" },
  "/ecom-partner-calculator":  { itemName: "ecommerce_partner_calculator",  itemType: "partner_calculator" },
  "/partners/integrations":    { itemName: "ecommerce_integrations",        itemType: "partner_service" },
  "/refer-a-friend":           { itemName: "refer_a_friend",                itemType: "partner_service" },
};

const SOLUTIONS = [
  { label: "Card Payment Machines", to: "/card-machines" },
  { label: "Payment Gateway", to: "/payment-gateway" },
  { label: "EPOS Systems", to: "/epos-systems" },
  { label: "Online Payments", to: "/online-payments" },
  { label: "Online Ordering", to: "/online-ordering" },
  { label: "Phone Payments", to: "/virtual-terminal" },
  { label: "Merchant Cash Advance", to: "/merchant-cash-advance" },
];

const TOOLS = [
  { label: "Payment Solution Finder", to: "/payment-finder" },
  { label: "Savings Calculator", to: "/switch-and-save" },
  { label: "Cost of Cash Calculator", to: "/cost-of-cash" },
  { label: "Cash vs Card Comparator", to: "/cash-vs-card" },
  { label: "In-Person Quote", to: "/quote/in-person" },
  { label: "Online Quote", to: "/quote/online" },
  { label: "Overpaying Scorecard", to: "/overpaying-scorecard" },
  { label: "Hidden Fees Checker", to: "/hidden-fees-checker" },
  { label: "Switch vs Stay", to: "/switch-vs-stay" },
  { label: "Statement Review", to: "/statement-review" },
];

const INDUSTRIES = [
  { label: "All Industries", to: "/industries" },
  { label: "Hospitality", to: "/industries/hospitality" },
  { label: "Pubs & Bars", to: "/industries/pubs" },
  { label: "Cafes & Coffee Shops", to: "/industries/cafes" },
  { label: "Takeaways", to: "/industries/takeaways" },
  { label: "Retail", to: "/industries/retail" },
  { label: "Personal Care", to: "/industries/personal-care" },
  { label: "E-Commerce", to: "/industries/e-commerce" },
  { label: "Fitness", to: "/industries/fitness" },
  { label: "Pet Services", to: "/industries/pet-services" },
  { label: "Beauty & Hair", to: "/industries/beauty" },
];

const LOCATIONS = [
  { label: "All Locations", to: "/locations" },
  { label: "Liverpool", to: "/locations/liverpool" },
  { label: "Manchester", to: "/locations/manchester" },
  { label: "Chester", to: "/locations/chester" },
  { label: "Warrington", to: "/locations/warrington" },
  { label: "Wirral", to: "/locations/wirral" },
];

const WORK_WITH_US = [
  { label: "Switch and Save", to: "/switch-and-save" },
  { label: "Partner Programme", to: "/partner-programme" },
  { label: "EPOS Partner Calculator", to: "/epos-partner-calculator" },
  { label: "E-commerce Partner Calculator", to: "/ecom-partner-calculator" },
  { label: "E-Commerce Integrations", to: "/partners/integrations" },
  { label: "Refer A Friend", to: "/refer-a-friend" },
];

const ABOUT_LINKS = [
  { label: "Our Story", to: "/about" },
  { label: "Purpose Beyond Payments", to: "/purpose-beyond-payments" },
  { label: "Meet The Team", to: "/about/team" },
  { label: "Our Experience", to: "/about/experience" },
  { label: "Charity Support", to: "/about/charity" },
  { label: "Sustainability", to: "/about/sustainability" },
  { label: "Our Values", to: "/about/values" },
  { label: "Careers", to: "/careers" },
  { label: "Case Studies", to: "/case-studies" },
  { label: "Customer Reviews", to: "/reviews" },
  { label: "Guides", to: "/guides" },
  { label: "FAQs", to: "/faqs" },
  { label: "Contact", to: "/contact" },
];

const FOOTER_SECTIONS = [
  { title: "Solutions", links: SOLUTIONS },
  { title: "Free Tools", links: TOOLS },
  { title: "Industries", links: INDUSTRIES },
  { title: "Locations", links: LOCATIONS },
  { title: "Work With Us", links: WORK_WITH_US },
  { title: "About", links: ABOUT_LINKS },
];

const LEGAL_LINKS = [
  { label: "Privacy Policy", to: "/privacy-policy" },
  { label: "Cookie Policy", to: "/cookie-policy" },
  { label: "Terms & Conditions", to: "/terms-and-conditions" },
  { label: "Modern Slavery Statement", to: "/modern-slavery-statement" },
  { label: "ICO Certificate", to: "/ico-certificate" },
  { label: "Business Insurance", to: "/business-insurance" },
  { label: "PSR Quotation Tool", to: "/psr-quotation-tool" },
  { label: "Payment Industry Terms", to: "/payment-industry-terms" },
];

const LOGO_URL = "https://cdn.candopayments.com/logos/cando_long_colour-inverse_web_transparent.svg";

function LinkList({ links, horizontal = false }) {
  // horizontal: used by the desktop "More" column so its three links lay out
  // as a flex-wrapped row inside the grid cell rather than a vertical stack.
  // Mobile path uses MobileAccordion (not this component) so this prop has
  // no effect at sub-lg breakpoints.
  const ulClass = horizontal
    ? "lg:flex lg:flex-wrap lg:gap-x-4 lg:gap-y-2 space-y-2.5 lg:space-y-0"
    : "space-y-2.5";
  return (
    <ul className={ulClass}>
      {links.map((link) => {
        const p = PARTNER_MAP[link.to];
        const onClick = p ? () => analytics.partnerClicked(p.itemName, { itemType: p.itemType, source: window.location.pathname }) : undefined;
        return (
          <li key={link.to}>
            <Link to={link.to} onClick={onClick} className="text-brand-grey hover:text-white text-sm transition-colors" data-measurement-owned={p ? "partner" : undefined}>{link.label}</Link>
          </li>
        );
      })}
    </ul>
  );
}

export function FooterColumn({ title, links }) {
  const ref = useRef(null);
  const firedRef = useRef(false);
  useEffect(() => {
    if (title !== "Work With Us" || !ref.current) return undefined;
    const el = ref.current;
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && !firedRef.current) {
          // Do not turn a refused view into a later replay. Retain the
          // observer until a newly observed, consent-eligible view records.
          if (analytics.partnerSectionViewed("work_with_us_section", { source: window.location.pathname })) {
            firedRef.current = true;
            obs.disconnect();
          }
        }
      });
    }, { threshold: 0.5 });
    obs.observe(el);
    return () => obs.disconnect();
  }, [title]);
  return (
    <div ref={ref}>
      <h4 className="font-heading font-bold text-sm uppercase tracking-wider text-white/80 mb-4">{title}</h4>
      <LinkList links={links} horizontal={title === "More"} />
    </div>
  );
}

function MobileAccordion({ title, links }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-white/10">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between py-3 text-left">
        <h4 className="font-heading font-bold text-sm uppercase tracking-wider text-white/80">{title}</h4>
        <ChevronDown size={16} className={`text-white/40 transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>
      <AnimatedAccordion isOpen={open}>
        <ul className="space-y-2.5 pb-4">
          {links.map((link) => {
            const p = PARTNER_MAP[link.to];
            const onClick = p ? () => analytics.partnerClicked(p.itemName, { itemType: p.itemType, source: window.location.pathname }) : undefined;
            return (
              <li key={link.to}>
                <Link to={link.to} onClick={onClick} className="text-brand-grey hover:text-white text-sm transition-colors" data-measurement-owned={p ? "partner" : undefined}>{link.label}</Link>
              </li>
            );
          })}
        </ul>
      </AnimatedAccordion>
    </div>
  );
}

function FooterContact() {
  return (
    <div>
      {/* The mark stacks under the wordmark rather than beside it: the inverse
          logo is ~295px at h-10 and the grid cell is ~312px, so a side-by-side
          row overflows into the Solutions column and crowds the mark. The 72px
          mark is ~43px wide, so the mt-6/mb-6 rhythm clears half its width
          above and below, per B Lab clear-space rules. */}
      <div className="mb-6">
        <img src={LOGO_URL} alt="CanDo Payments" width="1217" height="165" className="h-10 w-auto" />
        <div className="mt-6">
          <BCorpMark variant="white" testId="footer-bcorp-mark" />
        </div>
      </div>
      <ul className="space-y-2.5">
        <li><a href="tel:+448003682989" className="flex items-center gap-2 text-brand-grey hover:text-white text-sm transition-colors" data-testid="footer-phone"><Phone size={14} className="flex-shrink-0" /> 0800 368 2989</a></li>
        <li>
          <a
            href={whatsAppUrl("footer")}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => analytics.clickWhatsApp("footer")}
            className="flex items-center gap-2 text-brand-grey hover:text-white text-sm transition-colors"
            data-testid="footer-whatsapp"
          >
            {/* No space before the expression: an adjacent " " text node merges with
                the number in the react-snap snapshot and breaks hydration (#418). */}
            <WhatsAppIcon size={14} className="flex-shrink-0" />{WHATSAPP_NUMBER_DISPLAY}
          </a>
        </li>
        <li><a href="mailto:hello@CanDoPayments.com" className="flex items-center gap-2 text-brand-grey hover:text-white text-sm transition-colors" data-testid="footer-email"><Mail size={14} className="flex-shrink-0" /> hello@CanDoPayments.com</a></li>
        <li className="flex items-start gap-2 text-brand-grey text-sm"><MapPin size={14} className="mt-0.5 flex-shrink-0" /> The Technology Centre, Inward Way, Ellesmere Port, CH65 3EN</li>
      </ul>
      <SocialIcons variant="footer" className="mt-4" />
    </div>
  );
}

function FooterLegalDesktop() {
  return (
    <div className="hidden sm:flex flex-wrap items-center justify-center gap-3 text-xs text-brand-grey">
      {LEGAL_LINKS.map((link, i) => (
        <span key={link.to} className="contents">
          <Link to={link.to} className="hover:text-white transition-colors">{link.label}</Link>
          {i < LEGAL_LINKS.length - 1 && <span className="text-white/20">|</span>}
        </span>
      ))}
      <span className="text-white/20">|</span>
      <button type="button" onClick={openCookiePreferences} className="hover:text-white transition-colors" data-testid="cookie-settings-link">Cookie Settings</button>
      <span className="text-white/20">|</span>
      <a href="https://betterbusinessact.org/act-now/" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">Better Business Act Supporter</a>
    </div>
  );
}

function FooterCopyright() {
  return (
    <div className="flex flex-col md:flex-row items-center justify-between gap-4">
      {/* Single-expression child: adjacent text + {CURRENT_YEAR} children merge
          into one DOM text node in the react-snap snapshot and break hydration
          (React #418) on every prerendered page. */}
      <p className="text-brand-grey text-xs text-center md:text-left">{`\u00a9 ${CURRENT_YEAR} Can Do Payments Ltd. Company No. 11701057. VAT: GB311835329`}</p>
      <p className="text-brand-grey text-xs text-center md:text-right max-w-xl">CanDo Payments Ltd is an ISO and independent introducer of merchant services, partnering with Elavon Merchant Services (authorised by the Central Bank of Ireland, regulated in the UK by the PRA and FCA).</p>
    </div>
  );
}

export default function Footer() {
  return (
    <footer className="bg-brand-black text-white" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Desktop: full grid */}
        <div className="hidden lg:grid grid-cols-8 gap-8">
          <div className="col-span-2"><FooterContact /></div>
          {FOOTER_SECTIONS.map((s) => (
            <FooterColumn key={s.title} title={s.title} links={s.links} />
          ))}
        </div>

        {/* Mobile/Tablet: accordion */}
        <div className="lg:hidden">
          <div className="mb-6"><FooterContact /></div>
          {FOOTER_SECTIONS.map((s) => (
            <MobileAccordion key={s.title} title={s.title} links={s.links} />
          ))}
        </div>

        <div className="mt-12 pt-8 border-t border-white/10 space-y-6">
          <div className="flex flex-col items-center gap-4">
            <PaymentMethods showLabel={false} variant="light" size="sm" />
            <EcologiFooterBadge />
          </div>
          <FooterLegalDesktop />
          <div className="sm:hidden">
            <MobileAccordion title="Legal & Policies" links={LEGAL_LINKS} />
            <button type="button" onClick={openCookiePreferences} className="mt-3 text-brand-grey hover:text-white text-sm underline transition-colors" data-testid="cookie-settings-link-mobile">Cookie Settings</button>
          </div>
          <FooterCopyright />
        </div>
      </div>
    </footer>
  );
}
